'use client'

import { useEffect, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useAuth } from '@/contexts/AuthContext'
import { getWelcomeGroupChatId } from '@/lib/groups'
import { ExternalLink, Loader2, MessageCircle, X } from 'lucide-react'
import type { ApexPlatformCode } from '@/lib/apex-candidate'

/**
 * Which name the stats lookup actually matches on, per platform. PC is the one
 * people get wrong: it's the EA account name, not the Steam display name —
 * apexlegendsapi.com matches PC players on their EA (Origin) name only.
 */
export const APEX_PLATFORM_HINT: Record<ApexPlatformCode, string> = {
  PC: 'Use your EA ID, not your Steam name.',
  PS4: 'Use your PSN Online ID.',
  X1: 'Use your Xbox Gamertag, without the #.',
}

export const APEX_PLATFORM_LABEL: Record<ApexPlatformCode, string> = {
  PC: 'PC',
  PS4: 'PlayStation',
  X1: 'Xbox',
}

const SUPPORT_URL = 'https://gamerplug.featurebase.app/'
const EA_ACCOUNT_URL = 'https://myaccount.ea.com/cp-ui/aboutme/index'

/** Where to send someone who still can't find their account. */
export type ApexHelpDestination =
  | { kind: 'chat'; href: string }
  | { kind: 'support' }

const EXAMPLES: Record<ApexPlatformCode, { label: string; value: string; where: ReactNode }> = {
  PC: {
    label: 'EA ID',
    value: 'YourEAName',
    where: (
      <>
        Not your Steam name. It&apos;s under Account information on your{' '}
        <a
          href={EA_ACCOUNT_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="font-medium text-foreground underline underline-offset-2"
        >
          EA account
        </a>
        .
      </>
    ),
  },
  PS4: {
    label: 'Online ID',
    value: 'YourPSNName',
    where: 'PS5: Settings → Users and Accounts → Account → Profile.',
  },
  X1: {
    label: 'Gamertag',
    value: 'YourName1234',
    where: 'Type it without the #: YourName1234, not YourName#1234.',
  },
}

/**
 * Inline "Help" link plus the modal it opens: what the name looks like and
 * where to find it, for the platform already picked, and one way to get help
 * (the event chat when there is one, support otherwise).
 */
export function ApexNameHelp({
  platform,
  help,
  label = 'Help',
}: {
  platform: ApexPlatformCode
  help: ApexHelpDestination
  label?: string
}) {
  const [open, setOpen] = useState(false)

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="font-medium text-foreground underline underline-offset-2 transition-colors hover:text-foreground/80"
      >
        {label}
      </button>
      {open && (
        <ApexNameHelpModal platform={platform} help={help} onClose={() => setOpen(false)} />
      )}
    </>
  )
}

function ApexNameHelpModal({
  platform,
  help,
  onClose,
}: {
  platform: ApexPlatformCode
  help: ApexHelpDestination
  onClose: () => void
}) {
  const { label, value, where } = EXAMPLES[platform]
  const support = useSupportChatHref(help.kind === 'support')

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  // Portaled to <body>: the join prompt lives inside a backdrop-blur card,
  // which would otherwise become the containing block for this fixed overlay.
  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Find your Apex name"
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-sm space-y-4 rounded-2xl border border-white/10 bg-card p-5 text-sm shadow-2xl shadow-black/50"
      >
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold">
            Your {APEX_PLATFORM_LABEL[platform]} name
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="cursor-pointer rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-white/5 hover:text-foreground"
          >
            <X className="size-4" />
          </button>
        </div>

        {/* An illustration of the field, not a capture of the real platform UI. */}
        <div
          className="rounded-lg border-2 border-dashed border-primary/70 px-3 py-2"
          aria-hidden="true"
        >
          <p className="text-[11px] font-semibold uppercase tracking-wider text-foreground/60">
            {label}
          </p>
          <p className="font-medium text-foreground">{value}</p>
        </div>

        <p className="text-muted-foreground">{where}</p>

        {help.kind === 'chat' ? (
          <Link
            href={help.href}
            className="flex h-10 items-center justify-center gap-2 rounded-xl border border-white/15 font-medium transition-colors hover:bg-white/5"
          >
            <MessageCircle className="size-4" />
            Ask in the event chat
          </Link>
        ) : support === 'loading' ? (
          <button
            type="button"
            disabled
            className="flex h-10 w-full items-center justify-center gap-2 rounded-xl border border-white/15 font-medium text-muted-foreground"
          >
            <Loader2 className="size-4 animate-spin" />
            Contact support
          </button>
        ) : support ? (
          <Link
            href={support}
            className="flex h-10 items-center justify-center gap-2 rounded-xl border border-white/15 font-medium transition-colors hover:bg-white/5"
          >
            <MessageCircle className="size-4" />
            Contact support
          </Link>
        ) : (
          <a
            href={SUPPORT_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex h-10 items-center justify-center gap-2 rounded-xl border border-white/15 font-medium transition-colors hover:bg-white/5"
          >
            <ExternalLink className="size-4" />
            Contact support
          </a>
        )}
      </div>
    </div>,
    document.body
  )
}

/**
 * Link to the viewer's "Gamerplug x <gamertag>" chat with the team, 'loading'
 * while it resolves, or null when there's no chat (or the lookup failed) so
 * the caller falls back to the ticket form.
 */
function useSupportChatHref(enabled: boolean): string | 'loading' | null {
  const { session } = useAuth()
  const userId = session?.user?.id ?? null
  const pathname = usePathname()
  const localeSeg = pathname?.split('/')[1]
  const locale = localeSeg === 'es' ? 'es' : localeSeg === 'ja' ? 'ja' : 'en'
  const [chatId, setChatId] = useState<string | null | undefined>(undefined)

  useEffect(() => {
    if (!enabled || !userId) return
    let cancelled = false
    getWelcomeGroupChatId(userId)
      .catch(() => null)
      .then((id) => {
        if (!cancelled) setChatId(id)
      })
    return () => {
      cancelled = true
    }
  }, [enabled, userId])

  if (!enabled || !userId) return null
  if (chatId === undefined) return 'loading'
  return chatId ? `/${locale}/app/matches/group/${chatId}` : null
}
